import { useEffect, useMemo, useState } from 'react'

import { ErrorState, Skeleton } from '../../ui/Feedback'
import { LegalFooter } from '../../ui/LegalFooter'
import { LogoMark } from '../../ui/Logo'
import { Markdown } from '../docs/Markdown'
import { documentTitle, parseMarkdown, tableOfContents } from '../docs/parse'
import { legalDocuments, type LegalDocumentId } from './documents'

type Load = { state: 'loading' } | { state: 'ready'; source: string } | { state: 'error' }

/**
 * A legal document, readable without an account.
 *
 * Rendered outside the workspace shell, like the sign-in page, so it needs no
 * session and starts no API request: the terms have to be readable before
 * someone agrees to them.
 */
export function LegalPage({ document: id }: { document: LegalDocumentId }) {
  const legal = legalDocuments[id]
  const [load, setLoad] = useState<Load>({ state: 'loading' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let live = true
    setLoad({ state: 'loading' })
    legal.load().then(
      (source) => { if (live) setLoad({ state: 'ready', source }) },
      () => { if (live) setLoad({ state: 'error' }) },
    )
    return () => { live = false }
  }, [legal, attempt])

  useEffect(() => {
    const previous = document.title
    document.title = `${legal.title} · DDWS`
    return () => { document.title = previous }
  }, [legal])

  const blocks = useMemo(() => (load.state === 'ready' ? parseMarkdown(load.source) : []), [load])
  const contents = useMemo(() => tableOfContents(blocks), [blocks])
  // The reader drops the document's own `#` heading, so the page supplies the h1.
  const title = documentTitle(blocks) ?? legal.title

  // A link into a section (the Terms point at Privacy Policy §3) can only land
  // once the document has arrived.
  useEffect(() => {
    if (load.state !== 'ready') return
    const target = window.location.hash.slice(1)
    if (target) document.getElementById(decodeURIComponent(target))?.scrollIntoView()
  }, [load.state])

  return <div className="entry-layout">
    <header className="entry-bar legal-bar">
      <a className="entry-brand" href="/">
        <LogoMark className="entry-mark" />
        <span className="entry-brand-copy"><strong>DDWS</strong><span>Legal</span></span>
      </a>
      <nav className="legal-nav" aria-label="Legal documents">
        {Object.values(legalDocuments).map((entry) => (
          <a key={entry.id} href={entry.path} aria-current={entry.id === id ? 'page' : undefined}>{entry.title}</a>
        ))}
      </nav>
    </header>

    <main className="legal-main">
      <h1 className="legal-title">{title}</h1>
      {load.state === 'error' && (
        <ErrorState
          title={`We could not load the ${legal.title}`}
          body="The document is part of the console bundle, so this usually means the page was interrupted mid-download."
          onRetry={() => setAttempt((value) => value + 1)}
        />
      )}
      {load.state === 'loading' && (
        <div className="skeleton-stack" aria-label={`Loading the ${legal.title}`}>
          <Skeleton /><Skeleton /><Skeleton />
        </div>
      )}
      {load.state === 'ready' && (
        <div className="docs-layout">
          <nav className="docs-toc" aria-label="On this page">
            <p className="docs-toc-label">On this page</p>
            <ol>
              {contents.map((entry) => (
                <li key={entry.id} className={entry.level === 3 ? 'docs-toc-sub' : undefined}>
                  <a href={`#${entry.id}`}>{entry.text}</a>
                </li>
              ))}
            </ol>
          </nav>
          <article className="docs-article">
            <Markdown blocks={blocks} />
          </article>
        </div>
      )}
    </main>

    <footer className="entry-footer"><LegalFooter /></footer>
  </div>
}
